import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Download, Copy, Instagram, Check, PlayCircle } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LicenseShare() {
  const { licenseNo } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/license/${licenseNo}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "License not found"));
  }, [licenseNo]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success("License link copied — ready for Instagram, WhatsApp, anywhere.");
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#F2EBD8" });
      const link = document.createElement("a");
      link.download = `License-to-Love-${licenseNo}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Downloaded — ready to share to your story.");
    } catch (e) {
      toast.error("Download failed — try again.");
    } finally { setDownloading(false); }
  };

  if (err) return <div className="min-h-screen paper flex items-center justify-center text-ink/60">{err}</div>;
  if (!data) return <div className="min-h-screen paper flex items-center justify-center text-ink/40">Loading license…</div>;

  const issued = new Date(data.issued_at || Date.now()).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
  const licensed = data.license_status === "licensed";

  return (
    <div className="min-h-screen paper py-10 px-6">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" data-testid="ls-home"><Logo /></Link>
          <div className="text-xs font-mono text-ochre-300">{data.license_no}</div>
        </div>

        {/* 9:16 STORY CARD */}
        <div
          ref={cardRef}
          data-testid="license-card"
          style={{
            width: "100%",
            aspectRatio: "9/16",
            background: "linear-gradient(180deg, #F2EBD8 0%, #E7DBBE 100%)",
            borderRadius: 24,
            padding: "44px 32px",
            position: "relative",
            border: "2px double #D4A574",
            boxShadow: "0 24px 60px rgba(14,18,20,0.2)",
            overflow: "hidden",
            fontFamily: "'Instrument Sans', sans-serif",
            color: "#0E1214",
          }}
        >
          {/* Corner marks */}
          <div style={{ position: "absolute", top: 20, left: 24, fontSize: 10, letterSpacing: "0.28em", color: "#A85B2A", fontWeight: 600 }}>WHOLESOME™</div>
          <div style={{ position: "absolute", top: 20, right: 24, fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: "#0E1214" }}>No. {data.license_no}</div>

          {/* Header ornament */}
          <div style={{ textAlign: "center", marginTop: 40, fontSize: 10, letterSpacing: "0.32em", color: "#2E4A3B", fontWeight: 600 }}>
            · · · CERTIFICATE OF CHARACTER · · ·
          </div>

          {/* Big title */}
          <div style={{ textAlign: "center", marginTop: 22, fontFamily: "'Fraunces', serif", fontStyle: "italic", fontSize: 68, lineHeight: 0.95, color: "#0E1214", fontWeight: 400 }}>
            License<br/>to Love
          </div>

          {/* Recipient */}
          <div style={{ textAlign: "center", marginTop: 38, fontSize: 13, color: "rgba(14,18,20,0.7)", maxWidth: 340, marginLeft: "auto", marginRight: "auto", lineHeight: 1.5 }}>
            This is to certify that
          </div>
          <div style={{ textAlign: "center", marginTop: 10, fontFamily: "'Fraunces', serif", fontStyle: "italic", fontSize: 34, color: "#2E4A3B", fontWeight: 500 }}>
            {data.member_name}
          </div>
          <div style={{ textAlign: "center", marginTop: 10, fontSize: 13, color: "rgba(14,18,20,0.7)", maxWidth: 340, marginLeft: "auto", marginRight: "auto", lineHeight: 1.5 }}>
            has been vouched for as a kind and genuine person by {data.vouches_confirmed} human{data.vouches_confirmed === 1 ? "" : "s"} who know{data.vouches_confirmed === 1 ? "s" : ""} them well.
          </div>

          {/* Vouchers row */}
          {data.vouches.length > 0 && (
            <div style={{ marginTop: 34, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>
              <div style={{ color: "#A85B2A", letterSpacing: "0.18em", marginBottom: 8 }}>VOUCHED BY</div>
              {data.vouches.slice(0, 3).map((v, i) => (
                <div key={i} style={{ color: "#0E1214", marginTop: 3 }}>
                  {v.voucher_name} · {v.relationship}{v.has_video ? " · with video" : ""}
                </div>
              ))}
            </div>
          )}

          {/* Seal */}
          <div style={{ position: "absolute", left: "50%", bottom: 130, transform: "translateX(-50%) rotate(-6deg)" }}>
            <div style={{
              width: 108, height: 108, borderRadius: 999,
              background: "radial-gradient(circle at 30% 30%, #E4C79B, #A46D3E)",
              color: "#F2EBD8", display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "'Fraunces', serif", fontStyle: "italic", fontSize: 15, textAlign: "center", lineHeight: 1.2,
              border: "3px double rgba(242,235,216,0.55)",
              boxShadow: "0 8px 24px rgba(164,109,62,0.4), inset 0 -6px 12px rgba(0,0,0,0.15), inset 0 4px 10px rgba(255,255,255,0.4)"
            }}>
              Character<br/><em>Verified</em>
            </div>
          </div>

          {/* Bottom */}
          <div style={{ position: "absolute", left: 32, right: 32, bottom: 46, fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, color: "rgba(14,18,20,0.6)", display: "flex", justifyContent: "space-between" }}>
            <div>Issued · {data.city || "Ballarat, VIC"}<br/>{issued}</div>
            <div style={{ textAlign: "right" }}>Wholesome™<br/>Registrar of Kindness</div>
          </div>

          <div style={{ position: "absolute", bottom: 18, left: 0, right: 0, textAlign: "center", fontFamily: "'Caveat', cursive", fontSize: 20, color: "#A46D3E" }}>
            licencetolove.com
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <button data-testid="ls-download" onClick={downloadCard} disabled={downloading}
            className="btn-pill bg-ink text-parchment hover:bg-ivy flex items-center justify-center gap-2 disabled:opacity-50">
            <Download size={16} /> {downloading ? "Preparing…" : "Download for story"}
          </button>
          <button data-testid="ls-copy" onClick={copyLink}
            className="btn-pill bg-white border border-ink/10 text-ink hover:bg-parchment-50 flex items-center justify-center gap-2">
            {copied ? <><Check size={16} /> Copied</> : <><Copy size={16} /> Copy link</>}
          </button>
        </div>

        <div className="mt-4 text-center text-xs text-ink/60">
          <Instagram size={12} className="inline mr-1" /> Download and drop into your Instagram Story
        </div>

        {/* Vouch stories below the fold */}
        {data.vouches.length > 0 && (
          <div className="mt-10">
            <div className="eyebrow text-center">What they said about {data.member_first_name}</div>
            <div className="mt-4 space-y-4">
              {data.vouches.map((v, i) => (
                <div key={i} className="card-paper p-6" data-testid={`share-vouch-${i}`}>
                  <div className="flex items-center justify-between text-xs font-mono text-ochre-300">
                    <span>{v.voucher_name?.toUpperCase()} · {v.relationship?.toUpperCase()}</span>
                    {v.has_video && <span className="flex items-center gap-1 text-ivy"><PlayCircle size={12}/> VIDEO</span>}
                  </div>
                  <blockquote className="mt-3 font-display italic text-ink leading-relaxed" style={{ fontFamily: "Fraunces" }}>
                    "{v.kind_story}"
                  </blockquote>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 text-center space-y-3">
          <Link
            to={`/ledger/${data.license_no}`}
            className="btn-pill bg-ink text-parchment hover:bg-ivy inline-flex items-center gap-2"
            data-testid="ls-view-ledger"
          >
            See {data.member_first_name}'s Kind Ledger →
          </Link>
          <div>
            <Link to="/register" className="btn-pill bg-ivy text-parchment hover:bg-ivy-600 inline-flex" data-testid="ls-join">
              Request your own License to Love →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
